import { DefaultView } from "@redotech/effect-api-shared/models/support-ticket/default-view";

export type SupportNavEntry =
  | { kind: "defaultView"; id: DefaultView }
  | { kind: "view"; id: string }
  | { kind: "folder"; id: string };

interface OrderSupportNavEntriesInput {
  ordering: readonly { id: string; type: "view" | "folder" }[];
  topDefaultViewIds: readonly DefaultView[];
  bottomDefaultViewIds: readonly DefaultView[];
  folderedDefaultViewIds: ReadonlySet<string>;
  folders: readonly { id: string }[];
  folderlessViews: readonly { id: string; position: number }[];
}

/**
 * Returns a flat ordered list of nav entry refs for the support sidebar.
 *
 * Layout:
 *   1. Top default views (not in any folder), in their canonical order.
 *   2. User-ordered middle items (folders + folderless views); newly-created
 *      items not yet in the saved ordering are appended — folders in
 *      server-returned order, views sorted by their server-side position.
 *   3. Bottom default views (not in any folder), in their canonical order.
 */
export function orderSupportNavEntries({
  ordering,
  topDefaultViewIds,
  bottomDefaultViewIds,
  folderedDefaultViewIds,
  folders,
  folderlessViews,
}: OrderSupportNavEntriesInput): SupportNavEntry[] {
  const top: SupportNavEntry[] = topDefaultViewIds
    .filter((dv) => !folderedDefaultViewIds.has(dv))
    .map((dv) => ({ kind: "defaultView" as const, id: dv }));

  const bottom: SupportNavEntry[] = bottomDefaultViewIds
    .filter((dv) => !folderedDefaultViewIds.has(dv))
    .map((dv) => ({ kind: "defaultView" as const, id: dv }));

  const folderIds = new Set(folders.map((f) => f.id));
  const viewIds = new Set(folderlessViews.map((v) => v.id));

  const seen = new Set<string>();
  const middle: SupportNavEntry[] = [];

  for (const item of ordering) {
    if (item.type === "folder" && folderIds.has(item.id)) {
      if (!seen.has(`folder:${item.id}`)) {
        seen.add(`folder:${item.id}`);
        middle.push({ kind: "folder" as const, id: item.id });
      }
    } else if (
      item.type === "view" &&
      viewIds.has(item.id) &&
      !seen.has(`view:${item.id}`)
    ) {
      seen.add(`view:${item.id}`);
      middle.push({ kind: "view" as const, id: item.id });
    }
  }

  for (const folder of folders) {
    if (!seen.has(`folder:${folder.id}`)) {
      seen.add(`folder:${folder.id}`);
      middle.push({ kind: "folder" as const, id: folder.id });
    }
  }

  const unorderedViews = folderlessViews
    .filter((v) => !seen.has(`view:${v.id}`))
    .slice()
    .sort((a, b) => a.position - b.position);
  for (const view of unorderedViews) {
    middle.push({ kind: "view" as const, id: view.id });
  }

  return [...top, ...middle, ...bottom];
}
